// Single-source-of-truth registry for SSE streaming endpoints.
//
// Hono cannot derive an event type from `new Response(stream, …)`, so streaming
// endpoints declare their input + event schemas here. Both backend route
// handlers (for input validation) and the typed frontend stream client (for
// path/input/event typing and runtime validation of parsed chunks) read from
// this registry — same shape on both sides of the wire.
//
// Lives in shared/ because frontends import from here. Imports must stay
// shared-safe: zod (allowed runtime), Prisma (type-only), no backend-only
// utilities.

import { z } from "zod";

// ── Shared scalar schemas (kept local to avoid escaping shared/) ────────

const mandatoryString = z.string().min(1);
const objectId = z
  .string()
  .regex(/^[a-f\d]{24}$/i, { message: "Invalid ObjectId format" });

// ── Event shape (shared across all 5 endpoints) ─────────────────────────
//
// The AI service emits QueryConversationResponse on the wire
// (/llm/conversations/query_stream and /llm/conversations/chat_with_paper_stream),
// serialized via `.model_dump(exclude_none=True)`, so every field is effectively
// optional in practice. The shape below covers what the backend can emit;
// consumers narrow per-endpoint by checking presence.

const documentMetadata = z.object({
  article_id: z.string().nullable().optional(),
  content_id: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
});

const document = z.object({
  page_content: z.string().nullable().optional(),
  metadata: documentMetadata,
});

// AI service emits History entries as { human?: string; ai?: string }.
const historyEntry = z.object({
  human: z.string().nullable().optional(),
  ai: z.string().nullable().optional(),
});

export const streamEvent = z.object({
  conversation_id: z.string().nullable().optional(),
  query: z.string().nullable().optional(),
  question: z.string().nullable().optional(),
  answer: z.string().nullable().optional(),
  source_documents: z.array(document).nullable().optional(),
  stream_id: z.string().nullable().optional(),
  history: z.array(historyEntry).nullable().optional(),
  trace_id: z.string().nullable().optional(),
  article_id: z.string().nullable().optional(),
  follow_up_questions: z.array(z.string()).nullable().optional(),
  error: z.string().nullable().optional(),
});

// ── Input schemas ───────────────────────────────────────────────────────

// NOTE: Chat-with-Paper (private + public) AND the general webapp
// conversations (create + continue) are NO LONGER in this registry. Their
// runtime moved to native Hono + Vercel AI SDK `streamText` routes (the agent
// runtime bricks), so the wire is the SDK's UI message stream consumed by
// `useChat` — not the `streamEvent` SSE this registry describes. Those routes
// validate their input inline and return a UI message stream response.

const adminConversationParam = z.object({ id: objectId });
const adminConversationJson = z.object({ question: mandatoryString });

// ── Registry ────────────────────────────────────────────────────────────

interface StreamEndpointDef {
  input: { param?: z.ZodObject; json: z.ZodObject };
  event: z.ZodObject;
}

export const streamEndpoints = {
  "/admin/conversations/:id/stream": {
    input: { param: adminConversationParam, json: adminConversationJson },
    event: streamEvent,
  },
} as const satisfies Record<string, StreamEndpointDef>;

export type StreamPath = keyof typeof streamEndpoints;

type Entry<P extends StreamPath> = (typeof streamEndpoints)[P];

export type StreamEvent<P extends StreamPath> = z.infer<Entry<P>["event"]>;

export type StreamInput<P extends StreamPath> = {
  [K in keyof Entry<P>["input"]]: z.infer<
    Extract<Entry<P>["input"][K], z.ZodType>
  >;
};
